import { useMemo, useState } from 'react';
import { Button, Combobox, Field, Input, Modal, type ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import type { AccountLeadPeriod, AccountLeadPeriodUpdateInput } from '@/lib/types';
import { useUserOptions } from '@/lib/use-user-options';
import { USER_GROUP_SLUGS } from '@/lib/user-groups';
import { accountLeadErrorMessage, todayIsoUtc, validateLeadPeriods } from '@/lib/account-lead-history';

/*
 * AccountLeadPeriodModal: add or edit one period of a company's account lead
 * history (owner decisions 5 and 7, 2026-10-25).
 *
 * The lead picker offers the `account_lead` group only (the server refuses
 * anybody else). "To" blank = the open (current) period. Overlaps and the
 * single-open-period rule are the server's; this checks only what a single
 * row can get wrong (order, future dates) so Save stays honest.
 *
 * The modal owns its request: on success it hands the server's refreshed list
 * to `onSaved`. An edit sends only the keys that changed.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface AccountLeadPeriodModalProps {
  open: boolean;
  companyId: number;
  companyName: string;
  /** The period to edit; null adds a new one. */
  period: AccountLeadPeriod | null;
  /** The company's whole history as last read, for the overlap / single-open checks. */
  periods?: readonly AccountLeadPeriod[];
  onClose: () => void;
  onSaved: (periods: AccountLeadPeriod[]) => void;
}

export function AccountLeadPeriodModal(props: AccountLeadPeriodModalProps) {
  if (!props.open) return null;
  return <ModalBody key={props.period?.id ?? 'new'} {...props} />;
}

function errorText(caught: unknown): string {
  if (caught instanceof ApiError) {
    return (
      accountLeadErrorMessage(caught.code) ??
      caught.formMessage ??
      caught.message
    );
  }
  return caught instanceof Error ? caught.message : 'The period could not be saved.';
}

function ModalBody({
  companyId,
  companyName,
  period,
  periods = [],
  onClose,
  onSaved,
}: AccountLeadPeriodModalProps) {
  const isEdit = period !== null;
  const [today] = useState(() => todayIsoUtc());
  const { loading, failed, byGroup } = useUserOptions(true, true);
  const leadStaff = byGroup[USER_GROUP_SLUGS.accountLead];

  const [userId, setUserId] = useState(period ? String(period.user.id) : '');
  const [startsOn, setStartsOn] = useState(period?.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(period?.endsOn ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo<ComboboxOption[]>(() => {
    const rows: ComboboxOption[] = leadStaff.users.map((user) => ({
      value: String(user.id),
      label: user.name,
      hint: user.email,
    }));
    // Keep the edited period's lead selectable even when they have left the group.
    if (period && !rows.some((row) => row.value === String(period.user.id))) {
      rows.unshift({
        value: String(period.user.id),
        label: period.user.name,
        hint: period.user.email,
      });
    }
    return rows;
  }, [leadStaff.users, period]);

  const startValid = ISO_DATE.test(startsOn);
  const endValid = endsOn === '' || ISO_DATE.test(endsOn);
  const startInFuture = startValid && startsOn > today;
  const endInFuture = endValid && endsOn !== '' && endsOn > today;
  const endBeforeStart = startValid && endValid && endsOn !== '' && endsOn < startsOn;

  const patch: AccountLeadPeriodUpdateInput = {};
  if (period) {
    if (userId !== String(period.user.id)) patch.userId = Number(userId);
    if (startsOn !== period.startsOn) patch.startsOn = startsOn;
    const nextEnd = endsOn === '' ? null : endsOn;
    if (nextEnd !== period.endsOn) patch.endsOn = nextEnd;
  }
  const unchanged = isEdit && Object.keys(patch).length === 0;

  let blockedBy: string | null = null;
  if (userId === '') blockedBy = 'Choose an account lead';
  else if (startsOn === '') blockedBy = 'Choose the From date';
  else if (!startValid || !endValid) blockedBy = 'Enter valid dates';
  else if (startInFuture || endInFuture) blockedBy = 'Dates cannot be in the future';
  else if (endBeforeStart) blockedBy = 'To cannot be before From';
  else if (unchanged) blockedBy = 'No changes to save';
  else {
    // Mirror the server's set rules against the rest of the history as last read.
    const candidate = [
      ...periods.filter((other) => other.id !== period?.id),
      { startsOn, endsOn: endsOn === '' ? null : endsOn },
    ];
    const clash = validateLeadPeriods(candidate, today)[0];
    if (clash) blockedBy = clash.message;
  }

  const close = () => {
    if (!busy) onClose();
  };

  const save = async () => {
    if (blockedBy !== null || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = period
        ? await api.updateAccountLeadPeriod(companyId, period.id, patch)
        : await api.createAccountLeadPeriod(companyId, {
            userId: Number(userId),
            startsOn,
            endsOn: endsOn === '' ? null : endsOn,
          });
      onSaved(result.data);
    } catch (caught) {
      setError(errorText(caught));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={close}
      title={`${isEdit ? 'Edit' : 'Add'} account lead period · ${companyName}`}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void save()}
            loading={busy}
            disabled={blockedBy !== null}
            tooltip={blockedBy ?? undefined}
          >
            {isEdit ? 'Save' : 'Add period'}
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {failed && <Notice tone="danger">Staff could not be loaded.</Notice>}

        <Field label="Account lead" required>
          {(fieldProps) => (
            <Combobox
              id={fieldProps.id}
              value={userId}
              onChange={setUserId}
              options={options}
              loading={loading}
              disabled={busy}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="From" required error={startInFuture ? 'Choose today or an earlier date.' : undefined}>
            {(fieldProps) => (
              <Input
                {...fieldProps}
                type="date"
                value={startsOn}
                max={today}
                required
                disabled={busy}
                invalid={startInFuture}
                onChange={(event) => setStartsOn(event.target.value)}
              />
            )}
          </Field>
          <Field
            label="To"
            error={
              endInFuture
                ? 'Choose today or an earlier date.'
                : endBeforeStart
                  ? 'Choose a date on or after From.'
                  : undefined
            }
          >
            {(fieldProps) => (
              <Input
                {...fieldProps}
                type="date"
                value={endsOn}
                min={startValid ? startsOn : undefined}
                max={today}
                disabled={busy}
                invalid={endInFuture || endBeforeStart}
                onChange={(event) => setEndsOn(event.target.value)}
              />
            )}
          </Field>
        </div>

        {error && <Notice tone="danger">{error}</Notice>}
      </form>
    </Modal>
  );
}
